import * as THREE from 'three';
import type { CameraConfig } from '../../cameraConfig';

type PrincipalPointShift = { px: number; py: number };

const DEFAULT_SHIFT: PrincipalPointShift = { px: 0, py: 0 };
const PRINCIPAL_POINT_ANIMATION_DURATION = 0.35;

export class PrincipalPointController {
  private principalPointMatrix = new THREE.Matrix4();
  private principalPointShift: PrincipalPointShift = { ...DEFAULT_SHIFT };
  private principalPointShiftStart: PrincipalPointShift = { ...DEFAULT_SHIFT };
  private principalPointShiftTarget: PrincipalPointShift = { ...DEFAULT_SHIFT };
  private principalPointAnimationStartTime = 0;
  private principalPointAnimationDuration = 0;
  private isPrincipalPointAnimationActive = false;
  private canvasWidth = 1;
  private canvasHeight = 1;

  constructor(
    private readonly camera: THREE.PerspectiveCamera,
    private readonly clock: THREE.Clock
  ) {
    const baseUpdateProjectionMatrix = camera.updateProjectionMatrix.bind(camera);
    camera.updateProjectionMatrix = () => {
      baseUpdateProjectionMatrix();
      this.applyPrincipalPointShift();
    };
  }

  setCanvasSize(width: number, height: number): void {
    this.canvasWidth = Math.max(1, width);
    this.canvasHeight = Math.max(1, height);
  }

  applyViewOffset(
    viewOffset: CameraConfig['viewOffset'],
    options: { animate?: boolean; duration?: number } = {}
  ): void {
    const fullWidth = Math.max(1, this.canvasWidth || window.innerWidth || 1);
    const fullHeight = Math.max(1, this.canvasHeight || window.innerHeight || 1);
    const animateShift = Boolean(options.animate);
    const animationDuration = options.duration;

    let targetShift: PrincipalPointShift = { ...DEFAULT_SHIFT };

    if (viewOffset?.enabled) {
      const clampOffset = (value: number | undefined, max: number): number =>
        Math.min(Math.max(typeof value === 'number' ? value : 0, 0), max);

      const left = clampOffset(viewOffset.left, fullWidth);
      const right = clampOffset(viewOffset.right, fullWidth);
      const top = clampOffset(viewOffset.top, fullHeight);
      const bottom = clampOffset(viewOffset.bottom, fullHeight);

      const viewportWidth = Math.max(1, fullWidth - left - right);
      const viewportHeight = Math.max(1, fullHeight - top - bottom);
      const centerX = Math.min(Math.max(left + viewportWidth / 2, 0), fullWidth);
      const centerY = Math.min(Math.max(top + viewportHeight / 2, 0), fullHeight);

      targetShift = {
        px: -((centerX / fullWidth) * 2 - 1),
        py: (centerY / fullHeight) * 2 - 1,
      };
    }

    this.startPrincipalPointShiftAnimation(targetShift, animateShift, animationDuration);
  }

  forceSync(): void {
    this.camera.updateProjectionMatrix();
  }

  update(currentTime: number): void {
    if (!this.isPrincipalPointAnimationActive) {
      return;
    }
    const duration =
      this.principalPointAnimationDuration > 0
        ? this.principalPointAnimationDuration
        : PRINCIPAL_POINT_ANIMATION_DURATION;
    const elapsed = currentTime - this.principalPointAnimationStartTime;
    const progress = duration === 0 ? 1 : Math.min(1, elapsed / duration);
    const eased =
      progress < 0.5
        ? 2 * progress * progress
        : -1 + (4 - 2 * progress) * progress;
    this.principalPointShift = {
      px: THREE.MathUtils.lerp(
        this.principalPointShiftStart.px,
        this.principalPointShiftTarget.px,
        eased
      ),
      py: THREE.MathUtils.lerp(
        this.principalPointShiftStart.py,
        this.principalPointShiftTarget.py,
        eased
      ),
    };
    if (progress >= 1) {
      this.principalPointShift = { ...this.principalPointShiftTarget };
      this.isPrincipalPointAnimationActive = false;
    }
    this.camera.updateProjectionMatrix();
  }

  private startPrincipalPointShiftAnimation(
    target: PrincipalPointShift,
    animate: boolean,
    animationDuration?: number
  ): void {
    const pxDelta = Math.abs(target.px - this.principalPointShift.px);
    const pyDelta = Math.abs(target.py - this.principalPointShift.py);
    if (!animate || (pxDelta < 0.0001 && pyDelta < 0.0001)) {
      this.setPrincipalPointShiftInstant(target);
      return;
    }
    this.principalPointShiftStart = { ...this.principalPointShift };
    this.principalPointShiftTarget = { ...target };
    this.principalPointAnimationStartTime = this.clock.getElapsedTime();
    this.principalPointAnimationDuration = typeof animationDuration === 'number'
      ? animationDuration
      : PRINCIPAL_POINT_ANIMATION_DURATION;
    this.isPrincipalPointAnimationActive = true;
  }

  private setPrincipalPointShiftInstant(shift: PrincipalPointShift): void {
    this.principalPointShift = { ...shift };
    this.principalPointShiftTarget = { ...shift };
    this.isPrincipalPointAnimationActive = false;
    this.camera.updateProjectionMatrix();
  }

  private applyPrincipalPointShift(): void {
    const near = this.camera.near;
    const far = this.camera.far;
    const fovRad = THREE.MathUtils.degToRad(this.camera.fov);
    const aspect = this.camera.aspect;

    const top = near * Math.tan(fovRad / 2);
    const bottom = -top;
    const height = top - bottom;
    const width = height * aspect;
    const left = -width / 2;
    const right = width / 2;

    const xShift = (this.principalPointShift.px * (right - left)) / 2;
    const yShift = (this.principalPointShift.py * (top - bottom)) / 2;

    const l = left + xShift;
    const r = right + xShift;
    const b = bottom + yShift;
    const t = top + yShift;

    const x = (2 * near) / (r - l);
    const y = (2 * near) / (t - b);
    const a = (r + l) / (r - l);
    const b2 = (t + b) / (t - b);
    const c = -(far + near) / (far - near);
    const d = (-2 * far * near) / (far - near);

    this.principalPointMatrix.set(
      x, 0, a, 0,
      0, y, b2, 0,
      0, 0, c, d,
      0, 0, -1, 0
    );

    this.camera.projectionMatrix.copy(this.principalPointMatrix);
    this.camera.projectionMatrixInverse.copy(this.camera.projectionMatrix).invert();
  }
}
